import React from 'react';

import { BackNavigation, LayoutSingleColumn, NamedLink, Page } from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import {
  AtAGlance,
  RelatedGuides,
  SellerResourceCTA,
  SellerResourceHeader,
  SellerResourceLayout,
  SellerResourceSection,
  Workflow,
} from './SellerResources';
import WhenSomethingGoesWrong from './WhenSomethingGoesWrong';
import css from './SellerResources.module.css';

const NewListingLink = ({ children, className }) => (
  <NamedLink className={className} name="NewListingPage">
    {children}
  </NamedLink>
);

const SellingOverview = () => (
  <Page
    title="Selling on PageHop | PageHop Books"
    description="Everything you need to go from a book on your shelf to a completed sale."
  >
    <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
      <main className={css.root}>
        <BackNavigation name="SellerHubPage" label="Back to PageHop 101" />
        <SellerResourceHeader
          title="Selling on PageHop"
          description="Everything you need to go from a book on your shelf to a completed sale."
        />
        <SellerResourceLayout
          topic="selling"
          railItems={[
            'At a glance',
            'From shelf to sold',
            'After it sells',
            'When something goes wrong',
          ]}
          keyFact={{
            title: 'Good listings sell trust first',
            copy:
              'Clear photos, accurate condition, and realistic pricing help buyers decide faster.',
          }}
        >
          <SellerResourceSection title="At a glance">
            <AtAGlance
              items={[
                ['book', 'List for free', 'Create listings without an upfront fee.'],
                ['coins', 'Set your own price', 'You choose the item price before publishing.'],
                [
                  'parcel',
                  'Buyer pays shipping',
                  'Shipping is paid by the buyer as part of the order.',
                ],
                [
                  'card',
                  'Get paid after delivery',
                  'Earnings are released after delivery and the 48-hour hold.',
                ],
              ]}
            />
          </SellerResourceSection>

          <SellerResourceSection title="From shelf to sold">
            <Workflow
              steps={[
                {
                  label: 'Create your listing',
                  description:
                    'Add the title, author, category, price, photos, condition, shipping details, and edition notes where relevant.',
                },
                {
                  label: 'Show the actual item',
                  description:
                    'Photograph the front, back, spine, page edges, and any flaws or special details.',
                },
                {
                  label: 'Describe condition clearly',
                  description:
                    'Choose the closest PageHop condition grade and disclose wear plainly.',
                },
                {
                  label: 'Publish to Marketplace',
                  description:
                    'Once published, your item can appear in Marketplace and your PageHop Shop.',
                },
                {
                  label: 'Manage from My Shelf',
                  description: 'Review listings, update details, and follow sales from My Shelf.',
                },
              ]}
            />
            <div className={css.inlineActions}>
              <NewListingLink className={css.secondaryAction}>Create Listing</NewListingLink>
              <NamedLink
                className={css.textAction}
                name="WishlistPage"
                to={{ search: '?tab=selling' }}
              >
                Go to My Shelf &rarr;
              </NamedLink>
            </div>
          </SellerResourceSection>

          <SellerResourceSection title="After it sells">
            <ol className={css.flow} aria-label="After it sells flow">
              {['Sold', 'Pack', 'Ship', 'Delivered', '48-hour hold', 'Payout'].map(step => (
                <li key={step}>{step}</li>
              ))}
            </ol>
          </SellerResourceSection>

          <WhenSomethingGoesWrong className={css.section} />

          <SellerResourceSection title="Helpful next steps">
            <div className={css.topicLinks}>
              <NamedLink name="HowToUseShelfyTopicPage" params={{ topic: 'condition' }}>
                Condition Guide
              </NamedLink>
              <NamedLink name="HowToUseShelfyTopicPage" params={{ topic: 'shipping' }}>
                Shipping
              </NamedLink>
              <NamedLink name="HowToUseShelfyTopicPage" params={{ topic: 'getting-paid' }}>
                Getting Paid
              </NamedLink>
            </div>
          </SellerResourceSection>
        </SellerResourceLayout>
        <RelatedGuides topic="selling" />
        <SellerResourceCTA title="Ready to list your first book?" />
      </main>
    </LayoutSingleColumn>
  </Page>
);

export default SellingOverview;
